import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { usePolling } from "../../features/messages/hooks/usePolling";
import { useMessagesLive } from "../../features/messages/live/useMessagesLive";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import { MessageThread } from "../../features/messages/models/MessageThread";
import {
  selectSendStatus,
  selectSessionCard,
  selectThreadMessages,
} from "../../features/messages/selectors/messages-selectors";
import {
  fetchMessages,
  fetchSessionCard,
  fetchOlderMessages,
  markThreadRead,
  sendMessage,
} from "../../features/messages/slice-extensions/messages-slice-extension";
import { LoadingStatus } from "../../features/sessions/models/LoadingStatus";
import { staffPaymentLink } from "../../features/messages/utils/paymentCardText";
import MessageComposer from "./MessageComposer";
import MessageRow from "./MessageRow";
import MuteThreadButton from "./MuteThreadButton";
import StaffOnlyToggle from "./StaffOnlyToggle";
import PaymentCard from "./PaymentCard";
import SessionCard from "./SessionCard";
import SystemLine from "./SystemLine";
import ThreadTitle from "./ThreadTitle";

interface ThreadViewProps {
  channel: MessagesChannel;
  thread: MessageThread;
  /** Shown while the thread has no messages. */
  emptyText: string;
  /** With a title the empty thread shows the empty-state drawing (PD-0038): disc, title, then `emptyText`. */
  emptyTitle?: string;
  /** Phone-only link back to the thread list; omitted where there is no list. */
  backTo?: string;
  /** Where the Session card's "Open" leads, given the Session's id. */
  sessionLink: (bookingId: string) => string;
  /** The portal's Money section for a payment card; the card's stored path when omitted. */
  paymentLink?: string;
}

/**
 * One conversation: history oldest at the top, the composer below; refetched on the live signal,
 * or every 30 s while the live connection is down.
 */
const ThreadView = ({
  channel,
  thread,
  emptyText,
  emptyTitle,
  backTo,
  sessionLink,
  paymentLink,
}: ThreadViewProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectThreadMessages(thread.id));
  const sendStatus = useAppSelector(selectSendStatus);
  const card = useAppSelector(selectSessionCard(thread.id));
  const isSession = thread.kind === "Session";
  const isStaff = channel.kind === "staff";
  const canBeStaffOnly = isStaff && thread.kind !== "Direct";
  const bottom = useRef<HTMLDivElement>(null);
  const items = state?.items ?? [];
  const newestId = items[0]?.id;

  const refresh = async () => {
    const result = await dispatch(fetchMessages({ channel, threadId: thread.id }));
    if (fetchMessages.fulfilled.match(result)) dispatch(markThreadRead({ channel, threadId: thread.id }));
    if (isSession) dispatch(fetchSessionCard({ channel, threadId: thread.id }));
  };
  const live = useMessagesLive(
    channel,
    {
      onThreadChanged: (threadId) => {
        if (threadId === thread.id) void refresh();
      },
      onResync: () => void refresh(),
    },
    thread.id,
  );
  usePolling(refresh, live !== "connected");

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [newestId]);

  const send = async (body: string, attachmentIds: string[]): Promise<boolean> => {
    const result = await dispatch(sendMessage({ channel, threadId: thread.id, body, attachmentIds }));
    if (sendMessage.fulfilled.match(result)) return true;
    toast.error(result.payload?.code === "messages_stopped" ? t("messages.stopped") : t("messages.sendFailed"));
    return false;
  };

  const loadOlder = () => {
    if (state?.nextBefore) {
      dispatch(fetchOlderMessages({ channel, threadId: thread.id, before: state.nextBefore }));
    }
  };

  return (
    <div className="flex flex-col">
      <div className="border-b border-grey-90 pb-3 dark:border-primary-20">
        {backTo && (
          <Link to={backTo} className="text-sm text-primary-40 hover:underline dark:text-primary-90 lg:hidden">
            {t("messages.back")}
          </Link>
        )}
        <div className="flex items-start justify-between gap-3">
          <ThreadTitle thread={thread} />
          <div className="flex shrink-0 items-center gap-3">
            {canBeStaffOnly && <StaffOnlyToggle thread={thread} />}
            <MuteThreadButton channel={channel} thread={thread} />
          </div>
        </div>
        {thread.participantNames.length > 1 && (
          <p className="text-sm text-grey-40 dark:text-grey-80">{thread.participantNames.join(", ")}</p>
        )}
        {thread.kind !== "Direct" && (
          <p className="text-sm text-grey-40 dark:text-grey-80">
            {[
              thread.hostName ? t("messages.thread.host", { name: thread.hostName }) : null,
              t("messages.thread.participants", { count: thread.participantCount }),
            ]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
        {canBeStaffOnly && thread.staffOnly && (
          <p id={`staff-only-hint-${thread.id}`} className="text-sm text-grey-40 dark:text-grey-80">
            {t("messages.staffOnly.hint")}
          </p>
        )}
        {isSession && card && <SessionCard card={card} openTo={sessionLink(card.bookingId)} />}
      </div>

      {state?.nextBefore && (
        <button type="button" className="btn btn-text self-center" onClick={loadOlder}>
          {t("messages.loadOlder")}
        </button>
      )}

      {state?.status === LoadingStatus.Failed && items.length === 0 && (
        <p className="py-6 text-sm text-red-700 dark:text-red-400">{t("messages.loadFailed")}</p>
      )}
      {state?.status === LoadingStatus.Idle &&
        items.length === 0 &&
        (emptyTitle ? (
          <ThreadEmptyState title={emptyTitle} text={emptyText} />
        ) : (
          <p className="py-6 text-sm text-grey-40 dark:text-grey-80">{emptyText}</p>
        ))}

      <ol className="divide-y divide-grey-90 dark:divide-primary-20" aria-live="polite">
        {[...items].reverse().map((message) =>
          message.systemLine ? (
            <SystemLine key={message.id} line={message.systemLine} sessionName={thread.title} />
          ) : message.card ? (
            <PaymentCard
              key={message.id}
              message={message}
              card={message.card}
              face={channel.kind}
              openTo={isStaff ? staffPaymentLink(message.card, thread.subjectId) : (paymentLink ?? message.card.portalPath)}
            />
          ) : (
            <MessageRow
              key={message.id}
              message={message}
              subjectId={thread.kind === "Direct" ? thread.subjectId : null}
              channel={channel}
            />
          ),
        )}
      </ol>
      <div ref={bottom} />

      {!isStaff && thread.messagesStopped ? (
        <p className="pt-3 text-sm text-grey-40 dark:text-grey-80" data-testid="messages-stopped-notice">
          {t("messages.stopped")}
        </p>
      ) : !isStaff && thread.staffOnly ? (
        <p className="pt-3 text-sm text-grey-40 dark:text-grey-80" data-testid="staff-only-notice">
          {t("messages.staffOnly.on")}
        </p>
      ) : (
        <MessageComposer
          channel={channel}
          threadId={thread.id}
          onSend={send}
          busy={sendStatus === LoadingStatus.Loading}
        />
      )}
    </div>
  );
};

/** The web's empty-state drawing: tinted card, 64 disc with the Messages glyph, the title and one line. */
const ThreadEmptyState = ({ title, text }: { title: string; text: string }) => (
  <div className="my-6 rounded-lg bg-grey-95 p-8 text-center dark:bg-primary-20" data-testid="thread-empty-state">
    <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-grey-85 dark:bg-grey-30">
      <MessageSquare size={32} strokeWidth={1.5} className="text-grey-50 dark:text-grey-70" aria-hidden="true" />
    </div>
    <h3 className="mb-2 text-lg font-medium text-grey-20 dark:text-white">{title}</h3>
    <p className="text-sm text-grey-40 dark:text-grey-80">{text}</p>
  </div>
);

export default ThreadView;
